import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardHeader,
  CardBody,
  Button,
  Row,
  Col,
  ModalFooter,
  Popover,
  PopoverHeader,
  PopoverBody,
} from "reactstrap";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  GetAvailableProgramLicenses,
  GetCustomerProgramLicenses,
} from "../../actions/adminQueries";
import { formatDate } from "../../Utilities/dateUtils";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";

import axios from "../../services/api/api";
import { API_BASE_URL } from "../../constants/api.constants";
import { toast } from "react-toastify";
import ExpiringLicense from "../ProgramLicense/ExpiringLicense";
import { FormattedMessage, useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

export default function AddProgramLicenseToSchoolModal({
  isOpen,
  toggle,
  school,
  initialLicense,
}) {
  const intl = useIntl();
  const [selectedLicenseToAdd, setSelectedLicenseToAdd] = useState("");
  const [fortNoxNbrPrgLic, setFortNoxNbrPrgLic] = useState("");
  const [fortNoxNbrStudLic, setFortNoxNbrStudLic] = useState("");
  const [fortNoxCostCenter, setFortNoxCostCenter] = useState("");
  const [validTo, setValidTo] = useState("");
  const [fortNoxReference, setFortNoxReference] = useState("");
  const [updateLicense, setUpdateLicense] = useState(null);
  const [animate, setAnimate] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [autoRenew, setAutoRenew] = useState(false);

  const queryClient = useQueryClient();
  const programLicenses = GetAvailableProgramLicenses();
  const customerLicenses = GetCustomerProgramLicenses(school?.id);

  useEffect(() => {
    if (initialLicense && customerLicenses.data) {
      const license = customerLicenses.data.find(
        (l) => l.id === initialLicense.id,
      );
      if (license) {
        handleUpdateLicense(license);
      }
    }
  }, [initialLicense, customerLicenses.data]);

  const handleChange = (selected) => {
    setSelectedLicenseToAdd(selected.displayName);
    setFortNoxNbrPrgLic(selected.fortNoxProgramLicenseNumber);
    setFortNoxNbrStudLic(selected.fortNoxStudentProgramLicenseNumber);
    setFortNoxCostCenter(selected.fortNoxCostCenter);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    switch (name) {
      case "fortNoxNbrPrgLic":
        setFortNoxNbrPrgLic(value);
        break;
      case "fortNoxNbrStudLic":
        setFortNoxNbrStudLic(value);
        break;
      case "fortNoxCostCenter":
        setFortNoxCostCenter(value);
        break;
      case "validTo":
        setValidTo(value);
        break;
      default:
        break;
    }
  };

  const addProgramLicenseToSchool = () => {
    if (!updateLicense) {
      axios
        .post(
          `${API_BASE_URL}/ProgramLicense?name=${
            selectedLicenseToAdd
          }&customerId=${school.id}
        &fortNoxNumber=${fortNoxNbrPrgLic.trim()}&fortNoxNumberStudent=${(fortNoxNbrStudLic ?? "").trim()}&fortNoxCostCenter=${fortNoxCostCenter.trim()}`,
        )
        .then(() => {
          toast.success(intl.formatMessage({ id: "ProgramLicenseAddedToSchool" }));
          queryClient.invalidateQueries({
            queryKey: ["FetchCustomerProgramLicenses", school.id],
          });
          setIsUpdated(true);
        })
        .catch((err) => {
          toast.warn(
            err && err.response && err.response.data
              ? formatServiceError(intl, err.response.data)
              : intl.formatMessage({ id: "CouldNotAddProgramLicenseToSchool" }),
          );
        });
    }

    if (updateLicense) {
      axios
        .put(
          `${API_BASE_URL}/ProgramLicense/UpdateProgramLicense?licenseId=${
            updateLicense.id
          }&fortNoxNumberProgramLicense=${fortNoxNbrPrgLic.trim()}&fortNoxNumberStudentLicense=${(fortNoxNbrStudLic ?? "").trim()}&fortNoxCostCenter=${
            fortNoxCostCenter
          }&validTo=${validTo}&autoRenew=${autoRenew}&fortNoxReference=${encodeURIComponent(fortNoxReference)}`,
        )
        .then(() => {
          toast.success(intl.formatMessage({ id: "ProgramLicenseUpdated" }));
          queryClient.invalidateQueries({
            queryKey: ["FetchCustomerProgramLicenses", school.id],
          });
          setIsUpdated(true);
        })
        .catch((err) => {
          toast.warn(
            err && err.response && err.response.data
              ? formatServiceError(intl, err.response.data)
              : intl.formatMessage({ id: "CouldNotUpdateProgramLicense" }),
          );
        });
    }

    setIsUpdated(false);
  };

  const handleUpdateLicense = (license) => {
    setUpdateLicense(license);
    setSelectedLicenseToAdd(license.id);
    setFortNoxNbrPrgLic(license.fortKnoxNumber);
    setFortNoxNbrStudLic(license.fortKnoxNumberStudentPackage);
    setFortNoxCostCenter(license.fortKnoxCostCenter);
    setValidTo(license.validTo);
    setAutoRenew(license.autoRenew);
    setFortNoxReference(license.fortNoxReference || "");
    setAnimate("animated slideInUp");
  };

  const quitEditing = () => {
    setAnimate("animated flipInY");
    setUpdateLicense(null);
    setSelectedLicenseToAdd("");
    setFortNoxNbrPrgLic(null);
    setFortNoxNbrStudLic(null);
    setFortNoxCostCenter(null);
  };

  if (programLicenses.isPending || customerLicenses.isPending) {
    return <div></div>;
  }

  var allLicenses = programLicenses.data ?? [];
  let schoolLicenses = customerLicenses.data ?? [];

  let availableLicenses = [];

  if (schoolLicenses !== null) {
    allLicenses.forEach((item) => {
      if (schoolLicenses.filter((x) => x.name === item).length === 0)
        availableLicenses.push(item);
    });
  }

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      toggle={() => toggle(false, null, null, isUpdated)}
    >
      <ModalHeader toggle={() => toggle(false, null, null, isUpdated)}>
        <FormattedMessage
          id="ProgramLicensesAtSchool"
          values={{ school: school !== null ? school.name : "" }}
        />
      </ModalHeader>
      <ModalBody>
        <Card
          className={animate + " " + (updateLicense ? "bg-light" : "")}
          style={{ zIndex: 9999 }}
        >
          <CardHeader>
            <Row className="m-0 p-0">
              <Col lg="10">
                <div className={updateLicense ? "d-none" : ""}>
                  <FontAwesomeIcon icon="circle-plus" />
                  &nbsp;<FormattedMessage id="AddProgramLicense" />
                </div>
                <div className={updateLicense ? "" : "d-none"}>
                  <FontAwesomeIcon icon="square-pen" />
                  &nbsp;
                  <FormattedMessage
                    id="UpdateLicenseName"
                    values={{ licenseName: updateLicense ? updateLicense.name : "" }}
                  />
                </div>
              </Col>
              <Col lg="2">
                <Button
                  id="helpBtn"
                  color="link"
                  className="float-end"
                  title={intl.formatMessage({ id: "ClickToReadHelp" })}
                  onClick={() => setPopoverOpen(!popoverOpen)}
                >
                  <FontAwesomeIcon
                    icon="circle-question"
                    className="text-info"
                  />
                </Button>

                <Popover
                  placement="bottom"
                  isOpen={popoverOpen}
                  target={"helpBtn"}
                >
                  <PopoverHeader>
                    <FormattedMessage id="HelpProgramLicense" />
                  </PopoverHeader>
                  <PopoverBody>
                    <strong>
                      <FormattedMessage id="CreateNewProgramLicenseForSchool" />
                    </strong>
                    <br />
                    <FormattedMessage id="ProgramLicenseHelpStep1" />
                    <br />
                    <FormattedMessage id="ProgramLicenseHelpStep2" />
                    <br />
                    <FormattedMessage id="ValidUntilDateNote" />
                    <br />
                    <br />
                    <strong>
                      <FormattedMessage id="UpdateExistingProgramLicense" />
                    </strong>
                    <br />
                    <FormattedMessage id="ProgramLicenseUpdateHelpStep1" />
                    <br />
                    <FormattedMessage id="ProgramLicenseUpdateHelpStep2" />
                    <br />
                    <FormattedMessage id="ProgramLicenseUpdateHelpStep3" />
                  </PopoverBody>
                </Popover>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Form>
              <FormGroup>
                <Select
                  placeholder={intl.formatMessage({ id: "ChooseProgramLicense" })}
                  className={updateLicense ? "d-none" : ""}
                  noOptionsMessage={() =>
                    intl.formatMessage({ id: "SchoolHasAllAvailableProgramLicenses" })
                  }
                  onChange={(selected) => handleChange(selected)}
                  options={availableLicenses.map((option) => ({
                    ...option,
                    label: option.displayName,
                    value: option.displayName,
                    clearableValue: true,
                  }))}
                />
              </FormGroup>
              <FormGroup>
                <Row className="m-0 p-0">
                  <Col lg="6">
                    <Label for="fortNoxNbrPrgLic">
                      <FormattedMessage id="ArticleNumberProgramLicense" />
                    </Label>
                    <Input
                      readOnly
                      type="text"
                      name="fortNoxNbrPrgLic"
                      id="fortNoxNbrPrgLic"
                      placeholder={intl.formatMessage({ id: "ArticleNumberProgramLicenseFortnox" })}
                      onChange={(event) => handleInputChange(event)}
                      defaultValue={
                        updateLicense
                          ? updateLicense.fortKnoxNumber
                          : fortNoxNbrPrgLic
                      }
                    />
                  </Col>
                  <Col lg="6">
                    <Label for="fortNoxNbrStudLic">
                      <FormattedMessage id="ArticleNumberStudentLicense" />
                    </Label>
                    <Input
                      readOnly
                      type="text"
                      name="fortNoxNbrStudLic"
                      id="fortNoxNbrStudLic"
                      placeholder={intl.formatMessage({ id: "ArticleNumberStudentLicenseFortnox" })}
                      onChange={(event) => handleInputChange(event)}
                      defaultValue={
                        updateLicense
                          ? updateLicense.fortKnoxNumberStudentPackage
                          : fortNoxNbrStudLic
                      }
                    />
                  </Col>
                  <Col lg="6">
                    <Label for="costCenter">
                      <FormattedMessage id="CostCenterShort" />
                    </Label>
                    <Input
                      readOnly
                      type="text"
                      name="fortNoxCostCenter"
                      id="fortNoxCostCenter"
                      placeholder={intl.formatMessage({ id: "CostCenterFortnox" })}
                      onChange={(event) => handleInputChange(event)}
                      defaultValue={
                        updateLicense
                          ? updateLicense.fortKnoxCostCenter
                          : fortNoxCostCenter
                      }
                    />
                  </Col>
                  <Col lg="6" className={updateLicense ? "" : "d-none"}>
                    <Label for="exampleDate">
                      <FormattedMessage id="ValidUntil" />
                    </Label>
                    <Input
                      type="date"
                      name="validTo"
                      id="validTo"
                      placeholder={intl.formatMessage({ id: "ProgramLicenseValidUntilPlaceholder" })}
                      onChange={(event) => handleInputChange(event)}
                      key={
                        updateLicense
                          ? formatDate(updateLicense.validTo, "yyyy-MM-dd")
                          : null
                      }
                      defaultValue={
                        updateLicense
                          ? formatDate(updateLicense.validTo, "yyyy-MM-dd")
                          : null
                      }
                    />
                  </Col>
                  <Col lg="12" className={updateLicense ? "" : "d-none"}>
                    <FormGroup>
                      <Label>
                        <FormattedMessage id="ReferenceForAutoRenewedLicense" />
                      </Label>
                      <Input
                        type="text"
                        placeholder={intl.formatMessage({ id: "ReferenceFortnox" })}
                        readOnly={!updateLicense}
                        value={fortNoxReference}
                        onChange={(e) => setFortNoxReference(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6" className={updateLicense ? "mt-2" : "d-none"}>
                    <FormGroup check>
                      <Label check>
                        <Input
                          type="checkbox"
                          name="autoRenew"
                          checked={autoRenew}
                          onChange={(e) => setAutoRenew(e.target.checked)}
                        />{" "}
                        <FormattedMessage id="AutoRenew" />
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </FormGroup>
            </Form>

            <div className="d-flex justify-content-end gap-2">
              <Button
                outline
                className={updateLicense ? "" : "d-none"}
                onClick={quitEditing}
              >
                <FormattedMessage id="Cancel" />
              </Button>
              <Button
                outline
                color="success"
                className="float-end"
                disabled={!selectedLicenseToAdd}
                onClick={() => addProgramLicenseToSchool()}
              >
                <FormattedMessage id={updateLicense ? "Update" : "Add"} />
              </Button>
            </div>
          </CardBody>
        </Card>

        <Card body>
          <Row>
            <Col lg="12" className="h5">
              <FormattedMessage
                id="SchoolNameProgramLicenses"
                values={{ school: school !== null ? school.name : "" }}
              />
            </Col>
          </Row>
          <Row className="mb-1 mt-2">
            <Col lg="4" className="font-weight-bold">
              <FormattedMessage id="ProgramLicense" />
            </Col>
            <Col lg="2" className="font-weight-bold">
              <FormattedMessage id="FortnoxArt" />
            </Col>
            <Col lg="2" className="font-weight-bold">
              <FormattedMessage id="FortnoxArtStudent" />
            </Col>
            <Col lg="2" className="font-weight-bold">
              <FormattedMessage id="ValidUntil" />
            </Col>
            <Col lg="2" className="font-weight-bold">
              <FormattedMessage id="AutoRenew" />
            </Col>
          </Row>
          {schoolLicenses &&
            schoolLicenses.map((license, index) => {
              let expiringLicenses = [];
              expiringLicenses.push(license);
              return (
                <Row key={index}>
                  <Col lg="4">
                    <Button
                      color="link"
                      title={intl.formatMessage({ id: "ClickToEditThisProgramLicense" })}
                      className="p-0 m-0"
                      onClick={() => handleUpdateLicense(license)}
                    >
                      {license.displayName}
                    </Button>
                  </Col>
                  <Col lg="2">{license.fortKnoxNumber}</Col>
                  <Col lg="2">{license.fortKnoxNumberStudentPackage}</Col>
                  <Col lg="2">
                    {formatDate(license.validTo, "yyyy-MM-dd")}
                    <ExpiringLicense
                      key={license.id}
                      licenses={expiringLicenses}
                    />
                  </Col>
                  <Col lg="2">
                    <FormattedMessage id={license.autoRenew ? "Yes" : "No"} />
                  </Col>
                </Row>
              );
            })}
        </Card>
      </ModalBody>
      <ModalFooter>
        <Button
          color="secondary"
          outline
          onClick={() => toggle(false, null, null, isUpdated)}
        >
          <FormattedMessage id="Close" />
        </Button>
      </ModalFooter>
    </Modal>
  );
}
